import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Link, Typography } from '@mui/material';
import { useConversationStore } from '../ConversationStoreProvider';
import { useTokens } from '../tokens/TokensProvider';
import { replaceStylesheetGlobals } from '../portal/theme/portalTheme';
import ProductCard from './product/ProductCard';
import WebPageCard from './WebPageCard';
import PortalItemCard from './PortalItemCard';
import useProductCardResponsive from './hooks/useProductCardResponsive';
import { getDualModeLinkSx } from './utils/linkStyles';
import { buildPortalAbsoluteHref, getPortalBasePath } from './portalNavigation';
const ReferencesSidebar = ({ message, openWebPagesInNewTab, onPortalRelativeNavigate, cardConfig, isMobileView, // eslint-disable-line @typescript-eslint/no-unused-vars
maxItems, showSeeMore = false, onProductClick, dualModeEnabled = false, }) => {
    const store = useConversationStore();
    const tokens = useTokens();
    const stylesheet = store?.stylesheet || {};
    // Responsive logic
    const { isHorizontal, shouldApplyResponsiveStyles, isTinyScreen, cardWidth, } = useProductCardResponsive({ clientAIConfig: cardConfig?.clientAIConfig });
    // Handle both object and array formats for carouselItems
    const carouselItemsArray = useMemo(() => {
        if (!message?.carouselItems) {
            return [];
        }
        return Array.isArray(message.carouselItems)
            ? message.carouselItems
            : Object.values(message.carouselItems);
    }, [message?.carouselItems]);
    // limit items to maxItems
    const items = carouselItemsArray.slice(0, maxItems || 8);
    const seeMoreText = cardConfig?.productFormatting?.seeMoreDetailsText
        || cardConfig?.clientAIConfig?.aiSearchConfig?.detailsButton?.text
        || tokens.getValue('seeMoreText');
    const renderedItems = useMemo(() => (Object.values(items).map((item) => {
        if (item.type === 'product') {
            return (_jsxs(Box, { sx: {
                    width: cardWidth,
                    flexShrink: 0,
                    marginBottom: showSeeMore ? '8px' : 0,
                }, children: [_jsx(ProductCard, { title: item.title, image: item.image, description: item.description, url: item.url, price: item.price, productFormatting: cardConfig?.productFormatting, product: item, onProductClick: onProductClick }, item.key), showSeeMore && item.url && (_jsx(Box, { sx: { marginTop: '8px', textAlign: 'right' }, children: _jsx(Link, { href: openWebPagesInNewTab
                                ? buildPortalAbsoluteHref(getPortalBasePath(), item.url)
                                : item.url, onClick: (e) => {
                                if (openWebPagesInNewTab) {
                                    e.preventDefault();
                                    window.open(buildPortalAbsoluteHref(getPortalBasePath(), item.url), '_blank', 'noopener,noreferrer');
                                }
                            }, target: openWebPagesInNewTab ? '_blank' : undefined, rel: openWebPagesInNewTab ? 'noopener noreferrer' : undefined, sx: {
                                fontSize: '12px',
                                ...getDualModeLinkSx(dualModeEnabled),
                                color: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                            }, children: _jsx(Typography, { variant: "caption", children: seeMoreText }) }) }))] }, item.key));
        }
        if (item.type === 'web') {
            return (_jsx(WebPageCard, { title: item.title, image: item.image, description: item.description, url: item.url, width: cardWidth, openInNewTab: openWebPagesInNewTab, config: cardConfig, onPortalRelativeNavigate: onPortalRelativeNavigate, itemType: item.type, organizationID: item.organizationID, entityId: item.id, slug: item.slug }, item.key));
        }
        const card = (_jsx(PortalItemCard, { config: cardConfig, title: item.title, image: item.image || item.screenshot, description: item.description, url: item.url || '', width: cardWidth, openInNewTab: openWebPagesInNewTab, onPortalRelativeNavigate: onPortalRelativeNavigate, product: item }, item.id));
        return (_jsx(Box, { sx: {
                width: cardWidth,
            }, children: card }, item?.target?.key));
    })), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        items,
        items.length,
        cardConfig,
        isHorizontal,
        shouldApplyResponsiveStyles,
        isTinyScreen,
        openWebPagesInNewTab,
        onPortalRelativeNavigate,
        showSeeMore,
        seeMoreText,
        stylesheet,
        onProductClick,
    ]);
    if (!items.length) {
        return null;
    }
    const finalGap = (shouldApplyResponsiveStyles || isTinyScreen) ? 0 : 3;
    const finalPaddingLeft = (shouldApplyResponsiveStyles || isTinyScreen) ? 0 : 2;
    const finalJustifyContent = (shouldApplyResponsiveStyles || isTinyScreen) ? 'center' : 'flex-start';
    return (_jsx(Box, { className: "mc-references-sidebar", sx: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: finalGap,
            paddingLeft: finalPaddingLeft,
            justifyContent: finalJustifyContent,
            ...(shouldApplyResponsiveStyles || isTinyScreen) && {
                margin: '0 auto',
                maxWidth: '100%',
            },
            '& > *': {
                flexShrink: 0,
            },
        }, children: renderedItems }));
};
ReferencesSidebar.defaultProps = {
    showSeeMore: false,
    onProductClick: undefined,
    onPortalRelativeNavigate: undefined,
    dualModeEnabled: false,
};
export default observer(ReferencesSidebar);
//# sourceMappingURL=ReferencesSidebar.js.map